import request from "@/http"
import http from "@/http/http"
import type { VisitRecordVO, BusinessPoiCooStatusDTO } from "@/api"
// ==============================================
export interface BusinessSceneList {
  // poiId
  poiId: string
  // 名字
  name: string
  // 地址
  address: string
  // 纬度
  latitude: number
  // 经度
  longitude: number
  // 到访客流指数
  visitFlow: number
  // 常驻人口客流指数
  residentFlow: number
  // 商户类型（SceneCustomerBusinessTag）
  businessType: string
  // 合作状态id
  cooStatusId: string
  // 合作状态
  cooStatusList: BusinessPoiCooStatusDTO[]
}

export interface KrCooperationRatio {
  // 合作方式（BusinessCooMode）
  cooMode: string
  // 合作状态
  ratio: number
}

interface BusinessSceneListVO {
  // 商户场景列表
  list: BusinessSceneList[]
  // kr目标初始已合作比例
  krInitCooperationRatio: KrCooperationRatio[]
  // 用户请求唯一标识, 任务派发时需要传参uuid（taskDispatcherUuid）
  uuid: string
  // 数据总数
  total: number
}

interface BusinessFlowFilter {
  // 指数类型（FlowFilterType）
  type: string
  // 指数单位（FlowFilterUnit）
  unit: string
  // 指数数值大小, 注: 不带单位
  count: number
}

interface BusinessSceneArgs {
  /**
   * 城市id
   */
  areaCode: string
  /**
   * 品牌id
   */
  brandId: string
  /**
   * 筛选类型（SceneCustomerQueryType）
   */
  queryType: string
  // queryType == STORE时传参
  /**
   * 半径
   */
  radius?: number
  /**
   * 网点id -- poiId
   */
  poiId?: string
  // queryType == CITY_RANGE时传参
  /**
   * 区县
   */
  districtId?: string
  /**
   * 街道
   */
  streetId?: string
  /**
   * 客流过滤方式
   */
  flowFilter: BusinessFlowFilter
  /**
   * 排序类型（BusinessSceneSortType）
   */
  sortType: string[]
  /**
   * true-升序，false-降序
   */
  isAsc: boolean[]
  /**
   * 存款合作状态（BusinessCooStatus）
   */
  depositFilter: string[]
  /**
   * 信用卡合作状态（BusinessCooStatus）
   */
  creditCardFilter: string[]
  /**
   * 贷款合作状态（BusinessCooStatus）
   */
  loanFilter: string[]
  /**
   * 商户收单合作状态（BusinessCooStatus）
   */
  businessFilter: string[]
  /**
   * 薪资代发合作状态（BusinessCooStatus）
   */
  salaryFilter: string[]
}

// 获取场景拓客—商户列表
export async function getBusinessSceneList(
  params: BusinessSceneArgs,
): Promise<BusinessSceneListVO> {
  try {
    const result = await http.post<BusinessSceneListVO>(`/sceneExpand/scene/business`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface StoreInfo {
  poiId: string
  storeName: string
}

interface BusinessBasicVO {
  // 商户名称
  name: string
  // 注册地址
  registerAddress: string
  // 商户状态
  status: string
  // 联系电话
  contactPhone: string
  // 联系人
  contactUsername: string
  // 银行商户id
  businessId: string
}

interface BusinessSceneCooStatusArgs {
  //  城市id
  areaCode: string
  //  品牌id
  brandId: string
  // 点位ID
  poiId: string
  // 商户基本信息
  businessBasic: BusinessBasicVO
  //  合作状态
  cooDetails: BusinessPoiCooStatusDTO[]
  // 拜访记录
  remark: string
}

/**
 * 更改商业场景合作状态
 * @returns 主键id
 */
export async function changeBusinessCooStatus(params: BusinessSceneCooStatusArgs): Promise<string> {
  try {
    const result = await http.post<string>(`/sceneExpand/scene/business/maintain`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface BusinessTaskDispatcherArgs {
  /**
   * 场景类型
   */
  sceneType: string
  /**
   * 用户请求唯一标识
   */
  taskDispatcherUuid: string
  /**
   * 筛选的商户poiId
   */
  poiIds: string[]
  /**
   * kr目标
   */
  krTarget: KrCooperationRatio[]
  /**
   * kr开始日期(pattern = "yyyy-MM-dd")
   */
  krBeginDate: string
  /**
   * kr结束日期(pattern = "yyyy-MM-dd")
   */
  krEndDate: string
  /**
   * 任务名称
   */
  taskName: string
  /**
   * 选择的成员手机号
   */
  selectUsers: string[]
  /**
   * 选择的部门ids
   */
  selectOrgIds: string[]
}

/**
 * 商业场景—任务派发
 * @returns true-成功，其他情况抛异常
 * */
export async function businessTaskDispatcher(params: BusinessTaskDispatcherArgs): Promise<boolean> {
  try {
    const result = await request.post<boolean>("/v1/industry/scene/businessTaskDispatcher", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface HourBasicItem {
  /**
   * 小时
   */
  hour: string
  /**
   * 指数 占比，保留四位小数
   */
  count: number
}

interface ProfileBasicItem {
  /**
   * 标签id
   */
  id: string
  /**
   * 标签名字
   */
  name: string
  /**
   * 占比，保留四位小数
   */
  ratio: number
  /**
   * 经度
   */
  longitude: number
  /**
   * 纬度
   */
  latitude: number
}

export interface BusinessPointProfileVO {
  // 名字
  name: string
  // 数据统计时间
  dataDate: string
  // 开业时间
  openingDate: string
  // 地址
  address: string
  // 距离-米
  distance: number
  // 网点名 -- 1.4版本新加字段
  storeName: string
  // 纬度
  latitude: number
  // 经度
  longitude: number
  // 上月客流（总客流）
  lastMonthFlowCount: number
  // 上月客流（常驻客流）
  residentFlowCount: number
  // 上月月度平均分时指数
  hourRatio: HourBasicItem[]
  // 年龄分布
  age: ProfileBasicItem[]
  // 消费能力
  consumption: ProfileBasicItem[]
  // 房产级别
  housePrice: ProfileBasicItem[]
  // 资产情况
  carAssets: ProfileBasicItem[]
}

export interface BusinessMaintenanceInfoDTO {
  phone: string
  userName: string
  // 业务场景（BusinessCooMode）
  cooMode: string
  // 业务合作状态（BusinessCooStatus）
  cooStatus: string
  // 拓展部门
  orgs: string
}

export type BusinessMaintenanceInfoVO = BusinessBasicVO & {
  // 场景商业类型 -- businessType和sceneType两者同义
  businessType: string
  // （SceneCustomerBusinessTag）
  sceneType: string
  // 与商户沟通已合作业务信息
  poiCooperation: BusinessMaintenanceInfoDTO[]
}

interface BusinessPointProfileArgs {
  // 城市id
  areaCode: string
  // 品牌id
  brandId: string
  // 上层接口透传的场景类型（SceneCustomerBusinessTag）
  sceneType: string
  // poi id
  poiId: string
  // tag
  profileTag: string
}

interface BusinessPointVO {
  name: string
  dataDate: string
  // 人群分析
  populationAnalysis: BusinessPointProfileVO
  // 维护信息
  maintainInfo: BusinessMaintenanceInfoVO
  // 拜访记录
  visitRecords: VisitRecordVO[]
}

// 商业场景—画像分析
export async function businessScenePointProfile(
  params: BusinessPointProfileArgs,
): Promise<BusinessPointVO> {
  try {
    const { profileTag, ...others } = params
    const result = await http.post<BusinessPointVO>(
      `/sceneExpand/scene/business/profile/${profileTag}`,
      others,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

interface BusinessInitKrCooperationRatioArgs {
  cityId: string
  brandId: string
  // 场景类型
  sceneType: string
  // 用户请求唯一标识
  taskDispatcherUuid: string
  // 筛选的商户poiId
  poiIds: string[]
}

// 获取任务派发时kr初始值—商户列表
export async function initKrCooperationRatio(
  params: BusinessInitKrCooperationRatioArgs,
): Promise<KrCooperationRatio[]> {
  try {
    const result = await request.post<KrCooperationRatio[]>(
      "/v1/industry/scene/initKrCooperationRatio",
      params,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}
